import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  Alert,
  Grid,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { POST } from "APIclient";
import properties from "constants/ApplicationConstants";

function ConfirmationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: "", type: "" });

  // Get data passed from AssignKRA page
  const {
    appraisalId,
    selectedSkills,
    selectedEmployees,
    technicalPrimary,
    competencyPrimary,
    competencySecondary,
    technicalSecondary,
  } = location.state || {};

  // Redirect if no data
  if (!location.state) {
    navigate("/manager/appraisal/assign");
    return null;
  }

  const handleConfirm = async () => {
    try {
      setSubmitting(true);

      // Get manager ID
      const managerId = parseInt(localStorage.getItem("empId") || sessionStorage.getItem("empId"));

      // Prepare employee-skill mappings
      const empSkills = selectedEmployees.map((employee) => ({
        empId: employee.empId,
        selectedTechnicalPrimary: selectedSkills, // All selected skills for this employee
      }));

      // Prepare payload according to KraAssignmentDTO structure
      const payload = {
        appraisalId: appraisalId,
        employeeSkillAssignments: empSkills,
      };

      console.log("Sending payload:", payload);
      console.log("Manager ID:", managerId);

      // Call the API
      const response = await POST(
        `manager/assign-kra?managerId=${managerId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${
              localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken")
            }`,
          },
        }
      );

      if (response.status === 200) {
        showNotification(
          "KRA assigned successfully! Notification emails will be sent to all selected employees.",
          "success"
        );

        // Navigate to success page or list after 2 seconds
        setTimeout(() => {
          navigate("/manager/appraisal/kraList"); // Adjust route as needed
        }, 2000);
      } else {
        showNotification("Failed to assign KRA. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error assigning KRA:", error);
      showNotification(
        error.response?.data?.message || "An error occurred while assigning KRA.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    // Go back to AssignKRA page with preserved data
    navigate("/manager/appraisal/assign", {
      state: location.state,
    });
  };

  const showNotification = (message, type) => {
    setNotification({ open: true, message, type });
    setTimeout(() => {
      setNotification({ open: false, message: "", type: "" });
    }, 5000);
  };

  // Get selected technical skills details
  const getSelectedSkillsDetails = () => {
    return technicalPrimary.filter((skill) => selectedSkills.includes(skill.paFormId));
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={1} pb={1}>
        <Card>
          <MDBox
            variant="gradient"
            sx={{ background: properties.colors.bgcolorbluethem }}
            bgColor="info"
            borderRadius="lg"
            coloredShadow="success"
            mx={1.5}
            mt={1}
            p={1.5}
            mb={0.5}
            textAlign="center"
          >
            <MDTypography variant="h5" fontWeight="medium" color="white">
              Confirmation Page
            </MDTypography>
            <MDTypography variant="body2" color="white" opacity={0.8} sx={{ fontSize: "0.9rem" }}>
              Review and confirm KRA assignment
            </MDTypography>
          </MDBox>

          <MDBox p={1.5}>
            {/* Information Alert */}
            <Alert severity="info" sx={{ mb: 1.5, py: 0.3 }}>
              <MDTypography variant="body2" sx={{ fontSize: "1rem", color: 'black' }}>
                Notification email will be sent to all selected employees. Please verify the details before confirming.
              </MDTypography>
            </Alert>

            <Grid container spacing={1.5}>
              {/* Selected Skills Section */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <MDBox p={1} sx={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
                    <MDTypography variant="subtitle2" fontWeight="medium">
                      Technical Skill Set Selected
                    </MDTypography>
                  </MDBox>
                  <MDBox p={1}>
                    {/* Skills List */}
                    {getSelectedSkillsDetails().length > 0 ? (
                      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        {getSelectedSkillsDetails().map((skill, index) => (
                          <Box
                            key={skill.paFormId}
                            sx={{
                              p: 0.75,
                              backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff',
                              borderBottom: index === getSelectedSkillsDetails().length - 1 ? 'none' : '1px solid #e0e0e0',
                            }}
                          >
                            <Typography variant="body2" sx={{ fontSize: "0.85rem", color: "#000" }}>
                              {index + 1}. {skill.skillSet}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Alert severity="warning" sx={{ py: 0.3 }}>
                        <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>No skills selected</Typography>
                      </Alert>
                    )}

                    {/* Total Count */}
                    <MDBox mt={1} p={0.75} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight="medium" sx={{ fontSize: "0.85rem" }}>
                        Total: {getSelectedSkillsDetails().length} skill(s) selected
                      </Typography>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>

              {/* Selected Employees Section */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <MDBox p={1} sx={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
                    <MDTypography variant="subtitle2" fontWeight="medium">
                      Employee List
                    </MDTypography>
                  </MDBox>
                  <MDBox p={1}>
                    {/* Employees List */}
                    {selectedEmployees.length > 0 ? (
                      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        {selectedEmployees.map((employee, index) => (
                          <Box
                            key={employee.empId}
                            sx={{
                              p: 0.75,
                              display: 'flex',
                              alignItems: 'center',
                              backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff',
                              borderBottom: index === selectedEmployees.length - 1 ? 'none' : '1px solid #e0e0e0',
                            }}
                          >
                            <Typography variant="body2" sx={{ minWidth: '25px', fontWeight: 'medium', fontSize: "0.85rem", color: "#000" }}>
                              {index + 1}.
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: "0.85rem", color: "#000" }}>
                              {`${employee.firstName} ${employee.middleName || ''} ${employee.lastName}`.trim()}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Alert severity="warning" sx={{ py: 0.3 }}>
                        <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>No employees selected</Typography>
                      </Alert>
                    )}

                    {/* Total Count */}
                    <MDBox mt={1} p={0.75} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight="medium" sx={{ fontSize: "0.85rem" }}>
                        Total: {selectedEmployees.length} employee(s) selected
                      </Typography>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <MDBox display="flex" justifyContent="flex-end" gap={1} mt={1.5}>
              <MDButton
                variant="outlined"
                color="dark"
                size="small"
                onClick={handleBack}
                disabled={submitting}
              >
                Back
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                onClick={handleConfirm}
                disabled={submitting}
                sx={{ background: properties.colors.bgcolorbluethem }}
              >
                {submitting ? (
                  <>
                    <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                    Confirming...
                  </>
                ) : (
                  "Confirm"
                )}
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>

        {/* Notification Alert */}
        {notification.open && (
          <MDBox
            sx={{
              position: "fixed",
              top: 20,
              right: 20,
              zIndex: 9999,
              minWidth: "300px",
            }}
          >
            <Alert
              severity={notification.type}
              onClose={() => setNotification({ open: false, message: "", type: "" })}
            >
              {notification.message}
            </Alert>
          </MDBox>
        )}
      </MDBox>
    </DashboardLayout>
  );
}

export default ConfirmationPage;